import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MapReadyStatus } from "../../../hooks/useMapReady";

const EXIT_MS = 300;

interface MapLoadingOverlayProps {
  ready: boolean;
  status: MapReadyStatus;
  isDarkMode: boolean;
}

export default function MapLoadingOverlay({
  ready,
  status,
  isDarkMode,
}: MapLoadingOverlayProps) {
  const { t } = useTranslation("lite");
  const [mounted, setMounted] = useState(!ready);

  useEffect(() => {
    if (!ready) {
      setMounted(true);
      return;
    }
    const id = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(id);
  }, [ready]);

  if (!mounted) return null;

  const themeClasses = {
    overlayBg: isDarkMode ? "bg-black/70" : "bg-white/70",
    text: isDarkMode ? "text-white" : "text-gray-900",
    spinner: isDarkMode
      ? "border-white/30 border-t-primary-500"
      : "border-gray-300 border-t-primary-500",
  };

  const detail =
    status === "loading-units"
      ? t("map.loadingUnitsFetching", "Obteniendo unidades…")
      : status === "connecting"
        ? t("map.loadingUnitsConnecting", "Conectando…")
        : t("map.loadingUnitsReceiving", "Recibiendo posiciones…");

  return (
    <div
      role="status"
      aria-live="polite"
      className={`absolute inset-0 z-[15] flex flex-col items-center justify-center gap-3 ${themeClasses.overlayBg} ${themeClasses.text} transition-opacity duration-300 ${
        ready ? "pointer-events-none opacity-0" : "opacity-100"
      }`}
    >
      <span
        className={`w-8 h-8 border-4 ${themeClasses.spinner} rounded-full animate-spin`}
      />
      {/* Un solo texto: el paso en curso. "Cargando unidades…" encima repetía
          lo mismo con menos información. key={detail} remonta el párrafo en
          cada cambio de paso para que entre con un fundido corto (opacidad). */}
      <p key={detail} className="text-sm font-medium animate-fadeIn">
        {detail}
      </p>
    </div>
  );
}
